/**
 * Minor Electrical Installation Works Certificate (MEIWC) - Validation Engine
 * Task 1.3.3: $Z_s$ threshold, insulation resistance >= 1.0 MΩ, RCD test verification
 */
import { ZsCalculator } from '../../utils/electrical/bs7671Tables.js';

const parseDevice = (type, rating, disconnectionTime = '0.4s', isRCD = false) => {
  const typeParts = type.split(' ');
  const base = typeParts[0]; // MCB, RCBO, BS
  const variant = typeParts.slice(1).join(' '); // 'Type B', '88-2', etc.

  return {
    type: base === 'MCB' ? 'MCB' : base === 'RCBO' ? 'RCBO' : 'Fuse',
    typeVariant: base === 'BS' ? `BS ${variant}` : variant,
    rating: parseInt(rating, 10) || 0,
    disconnectionTime,
    is30mARCD: isRCD
  };
};

export default {
  validate: (data) => {
    const errors = {};
    const warnings = {};

    // Part 1: Description of minor works
    if (!data.worksDescription?.certificateNumber?.trim()) {
      errors.certificateNumber = 'Certificate number is required';
    }
    if (!data.worksDescription?.dateOfWorks) {
      errors.dateOfWorks = 'Date of completion is required';
    }
    if (!data.worksDescription?.clientName?.trim()) {
      errors.clientName = 'Client name is required';
    }
    if (!data.worksDescription?.installationAddress?.trim()) {
      errors.installationAddress = 'Installation address is required';
    }
    if (!data.worksDescription?.descriptionOfWorks?.trim()) {
      errors.descriptionOfWorks = 'Description of works is required';
    }

    // Part 2: Earthing and bonding
    if (!data.earthingBonding?.earthingArrangement) {
      errors.earthingArrangement = 'Earthing arrangement is required';
    }
    if (!data.earthingBonding?.earthConductor) {
      errors.earthConductor = 'Earth conductor condition is required';
    }
    if (!data.earthingBonding?.mainBonding) {
      errors.mainBonding = 'Main bonding condition is required';
    }

    // Part 3: Circuit details & test results
    if (!data.circuitDetails || data.circuitDetails.length === 0) {
      errors.circuitDetails = 'At least one circuit must be recorded';
    } else {
      data.circuitDetails.forEach((circuit, index) => {
        const prefix = `circuit_${index}`;

        if (!circuit.circuitDescription?.trim()) {
          errors[`${prefix}_circuitDescription`] = 'Circuit description is required';
        }
        if (!circuit.protectiveDevice) {
          errors[`${prefix}_protectiveDevice`] = 'Protective device is required';
        }

        // $Z_s$ threshold check: measured Zs must not exceed max permitted
        if (circuit.protectiveDevice && circuit.deviceRating) {
          const device = parseDevice(circuit.protectiveDevice, circuit.deviceRating);
          const maxZs = ZsCalculator.getMaxZs(device);

          if (maxZs !== null) {
            // Auto-fill the max Zs if not provided
            if (circuit.maxZs === '' || circuit.maxZs === null || circuit.maxZs === undefined) {
              circuit.maxZs = maxZs;
            }

            // Compare measured Zs against max
            if (circuit.measuredZs !== '' && circuit.measuredZs !== null && circuit.measuredZs !== undefined) {
              const measured = parseFloat(circuit.measuredZs);
              if (measured > maxZs) {
                warnings[`${prefix}_measuredZs`] =
                  `Measured Zs (${measured} Ω) exceeds max permitted ` +
                  `(${maxZs.toFixed(2)} Ω) for ${circuit.protectiveDevice} ${circuit.deviceRating}A.`;
              }
            }
          }
        }

        // Insulation resistance >= 1.0 MΩ (BS 7671 requirement)
        if (circuit.insulationResistance !== '' && circuit.insulationResistance !== null && circuit.insulationResistance !== undefined) {
          const ir = parseFloat(circuit.insulationResistance);
          if (ir < 1.0) {
            warnings[`${prefix}_insulationResistance`] =
              `Insulation resistance (${ir} MΩ) is below the minimum of 1.0 MΩ.`;
          }
        }

        // RCD test verification
        if (circuit.rcdType && circuit.rcdType !== 'None') {
          // At 1Δn (100% of rated residual current), max trip time is 300 ms (BS 7671 41.3.1)
          if (circuit.rcdTripTime1x !== '' && circuit.rcdTripTime1x !== null && circuit.rcdTripTime1x !== undefined) {
            const trip1x = parseFloat(circuit.rcdTripTime1x);
            if (trip1x > 300) {
              warnings[`${prefix}_rcdTripTime1x`] =
                `RCD trip time at 1Δn (${trip1x} ms) exceeds the 300 ms limit.`;
            }
          } else if (circuit.measuredZs !== '' && circuit.measuredZs !== null && circuit.measuredZs !== undefined) {
            warnings[`${prefix}_rcdTripTime1x`] =
              'RCD present but no 1Δn trip time recorded - RCD test verification required.';
          }
        }

        // Test result consistency
        if (circuit.testResult) {
          const hasUnsatisfactoryReading =
            (circuit.measuredZs !== '' && circuit.measuredZs !== null && circuit.measuredZs !== undefined &&
              circuit.maxZs && parseFloat(circuit.measuredZs) > parseFloat(circuit.maxZs)) ||
            (circuit.insulationResistance !== '' && circuit.insulationResistance !== null && circuit.insulationResistance !== undefined &&
              parseFloat(circuit.insulationResistance) < 1.0);

          if (hasUnsatisfactoryReading && circuit.testResult === 'SATISFACTORY') {
            warnings[`${prefix}_testResult`] =
              'Test result marked SATISFACTORY but a measurement is out of limits.';
          }
        }
      });
    }

    // Part 4: Declaration
    if (!data.declaration?.engineerName?.trim()) {
      errors.engineerName = 'Engineer name is required';
    }
    if (!data.declaration?.engineerCardNumber?.trim()) {
      errors.engineerCardNumber = 'Engineer card number is required';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
      warnings
    };
  }
};